import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";
import type { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";

import {
  EDITABLE_ERROR_CODES,
  OptimizelyExperimentsEnum,
  RenewalPage,
} from "@/config/app-constant";
import { APPLICATION, PRODUCT_DOMAIN } from "@/constants";
import cookieHealthCheck from "@/hooks/api/cookie/health-check-cookie";
import { MarketingConsent } from "@/types/MarketingConsent";
import { Organiser } from "@/types/Organiser";
import { QuoteData, QuotePayload } from "@/types/quote/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import handleError, {
  ErrorResponse,
} from "@/utils/renewal-question-error-handle";
import { requestNewToken } from "@/utils/turnstile/turnstile.utils";

import createQuoteReference from "./API/createQuote";
import fetchQuoteData, { GetQuoteFilter } from "./API/getQuote";
import saveQuestionData from "./API/saveQuestion";
import updateQuoteReference from "./API/updateQuote";
// import { clearTurnstileToken } from "./quote.slice";

type CommonTypeOfRouterAndDispatch = {
  router: ReturnType<typeof useRouter>;
  dispatch: ReturnType<typeof useDispatch>;
};

const getQuoteData = createAsyncThunk(
  "quote/getQuote",

  async (
    {
      getQuoteFilter,
      router,
      dispatch,
    }: { getQuoteFilter: GetQuoteFilter } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue }
  ) => {
    try {
      const quote: QuotePayload | null | undefined =
        await fetchQuoteData(getQuoteFilter);

      return quote;
    } catch (err: unknown) {
      if (
        isAxiosError(err) &&
        err.response &&
        getQuoteFilter.affiliate === "quoteInit"
      ) {
        const errorCode = err.response.data?.errors?.[0]?.code;

        if (EDITABLE_ERROR_CODES.includes(errorCode)) {
          return err.response.data.data as QuotePayload;
        }
      }

      if (APPLICATION === RenewalPage) {
        await handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = await apiErrorMappings(err as AxiosError);
      if (errorObj?.errors?.[0]?.code === 4029) return null;
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const createQuoteOnQuestion = createAsyncThunk(
  "quote/createQuote",
  async (
    {
      payload,
      router,
      dispatch,
    }: { payload: QuotePayload } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue, getState }
  ) => {
    const state = getState() as { quote: QuoteData };
    const { turnstileToken, turnstileErrorCode, attributes } = state.quote;
    try {
      const getTurnstileHeaders = () => {
        if (!turnstileToken && attributes.domain !== "AVN") return undefined;
        return { headers: { "X-Turnstile-Token": turnstileToken } };
      };
      const updatedPayload = {
        ...payload,
        viewPage: payload.viewPage || null,
        attributes: {
          ...payload.attributes,
          turnstileDetails: turnstileErrorCode
            ? {
                ...(payload.attributes.turnstileDetails || {}),
                errorMessage: turnstileErrorCode || null,
              }
            : null,
        },
      };

      // add experiment details
      const experimentDetailsFromPayload = payload.attributes.experimentDetails;
      const turnstileEnabled = localStorage.getItem(
        OptimizelyExperimentsEnum.TURNSTILE
      );
      const contactPageShiftFlow = localStorage.getItem(
        OptimizelyExperimentsEnum.QUESTION_ORDER
      );

      const medicalEligibilityFlow = localStorage.getItem(
        OptimizelyExperimentsEnum.MEDICAL_ELIGIBILITY_FLOW
      );

      if (
        experimentDetailsFromPayload ||
        turnstileEnabled ||
        contactPageShiftFlow ||
        medicalEligibilityFlow
      ) {
        const experimentDetails = {
          ...(experimentDetailsFromPayload || {}),
          ...(turnstileEnabled && { turnstileFlow: turnstileEnabled }),
          ...(contactPageShiftFlow && { contactPageShiftFlow }),
          ...(medicalEligibilityFlow && { medicalEligibilityFlow }),
        };

        updatedPayload.attributes.experimentDetails = experimentDetails;
      } else {
        updatedPayload.attributes.experimentDetails = null;
      }

      const quote: QuotePayload | null | undefined = await createQuoteReference(
        updatedPayload,
        getTurnstileHeaders()
      );
      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = await apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;

      if (errorObj.httpCode === 400) {
        errorObj.validationEnable = true;
        // reset turnstile
        requestNewToken();
      }
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const updateQuoteOnQuestion = createAsyncThunk(
  "quote/updateQuote",
  async (
    {
      payload,
      router,
      dispatch,
    }: { payload: QuotePayload } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as { quote: QuoteData };
      const updatedPayload = {
        ...payload,
        referenceId: payload?.referenceId ?? state?.quote?.referenceId,
        version: state?.quote?.version ?? payload?.version,
        attributes: {
          ...payload?.attributes,
          domain: PRODUCT_DOMAIN,
          tripDetails: {
            ...payload?.attributes?.tripDetails,
            policyPackage:
              state?.quote?.attributes.tripDetails?.policyPackage ??
              payload?.attributes.tripDetails?.policyPackage,
          },
          marketingConsent: {
            ...((state?.quote?.attributes.marketingConsent ??
              payload?.attributes
                ?.marketingConsent) as unknown as MarketingConsent),
          },
          organiser: {
            ...(payload?.attributes?.organiser as unknown as Organiser),
            telephone:
              state?.quote?.attributes?.organiser?.telephone ??
              payload?.attributes?.organiser?.telephone,
          },
        },
        viewPage: payload.viewPage || null,
      } as QuotePayload;

      const quote: QuotePayload | null | undefined =
        await updateQuoteReference(updatedPayload);

      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = await apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      if (errorObj.httpCode === 400) {
        errorObj.validationEnable = true;
      }
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const saveQuestionDataBFF = createAsyncThunk(
  "quote/saveQuestionDataBFF",
  async (
    {
      payload,
      dispatch,
      router,
    }: {
      payload: QuotePayload;
    } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue, getState }
  ) => {
    const state = getState() as { quote: QuoteData };
    const { turnstileToken, attributes } = state.quote;
    try {
      const updatedPayload = {
        viewPage: payload.viewPage || null,
        attributes: {
          ...payload.attributes,
          domain: PRODUCT_DOMAIN,
        },
        channel: payload.channel || "ONLINE",
        type: payload.type || "IntermediateData",
      } as QuotePayload;

      const getTurnstileHeaders = () => {
        if (!turnstileToken && attributes.domain !== "AVN") return undefined;
        return { headers: { "X-Turnstile-Token": turnstileToken } };
      };

      const quote: QuotePayload | null | undefined = await saveQuestionData(
        updatedPayload,
        getTurnstileHeaders()
      );
      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }

      const errorObj = await apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const sessionCheck = createAsyncThunk(
  "quote/sessionCheck",
  async (
    { dispatch, router }: CommonTypeOfRouterAndDispatch,
    { rejectWithValue }
  ) => {
    try {
      const quote: QuotePayload | null | undefined = await cookieHealthCheck();
      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = await apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;

      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

export {
  getQuoteData,
  createQuoteOnQuestion,
  updateQuoteOnQuestion,
  saveQuestionDataBFF,
  sessionCheck,
};
